import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DemoDialog from "@/components/DemoDialog";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { useLang } from "@/i18n";
import { Search, ArrowRight, Play, CheckCircle2, Menu, X, ChevronDown, LayoutGrid, Truck, Wrench, Gauge, Cpu, MapPin, RefreshCw, Bell, FileBarChart, Settings, TrendingUp, Activity, BarChart3, Quote, Users, Boxes, ShieldCheck, Linkedin, Youtube, Twitter } from "lucide-react";

const RED = "#c00000";
const O = { fontFamily: "Outfit, sans-serif" };
const PHOTO = "https://images.unsplash.com/photo-1484557052118-f32bd25b45b5?w=1200&q=80";
const ROAD = "https://images.unsplash.com/photo-1615677679325-4a33c6d6b010?w=1600&q=80";
const EXCAVATOR = "https://images.unsplash.com/photo-1580192984971-1f0a3dcc535e?w=1000&q=80";
const GS = { filter: "grayscale(1) contrast(1.05)" };


function LogoA({ small, dark }) {
  return (
    <span className="flex items-center gap-2">
      <svg viewBox="0 0 32 32" className={small ? "w-6 h-6" : "w-8 h-8"} aria-hidden>
        <path d="M5 27 L16 5 L20 5 L11 27 Z" fill={RED} />
        <path d="M14 27 L24 8 L28 8 L20 27 Z" fill={RED} opacity="0.65" />
      </svg>
      <span className={`${small ? "text-base" : "text-xl"} font-extrabold tracking-tight ${dark ? "text-white" : "text-gray-900"}`} style={O}>Fleet<span style={{ color: RED }}>Area</span></span>
    </span>
  );
}

export default function EntwurfA() {
  const [demo, setDemo] = useState(false);
  const navigate = useNavigate();
  const openDemo = () => navigate("/kontakt");
  const { lang } = useLang();
  const de = lang === "de";
  const t = (d, e) => (de ? d : e);

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* HEADER */}
      <SiteNav />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0f1115]">
        {/* Vollflaechiges, unscharfes Schwarzweiss-Technologiebild */}
        <div aria-hidden className="absolute inset-0">
          <img src={PHOTO} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(1) blur(6px) brightness(0.7) contrast(1.08)", transform: "scale(1.12)" }} loading="eager" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(15,17,21,0.92) 0%, rgba(15,17,21,0.72) 42%, rgba(15,17,21,0.42) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(120% 80% at 100% 0%, rgba(192,0,0,0.22), transparent 55%)" }} />
        </div>
        {/* Rote Diagonale (oben rechts) */}
        <div aria-hidden className="absolute -top-2 right-0 hidden lg:block w-[52%] h-56 z-[1]" style={{ background: RED, clipPath: "polygon(100% 0, 100% 62%, 18% 0)", opacity: 0.9 }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          {/* Links: Text (weiss auf dunklem Bild) */}
          <div className="lg:col-span-5">
            <div className="text-xs font-bold tracking-[0.2em] text-gray-300 mb-5" style={O}>{t("VERBINDEN · ÜBERWACHEN · BEWEGEN", "CONNECT · MONITOR · MOVE")}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white" style={O}>
              <span style={{ color: "#c00000" }}>{t("Eine Plattform", "One platform")}</span> {t("für Fahrzeuge, Maschinen und IT", "for vehicles, machines and IT")}
            </h1>
            <p className="mt-6 text-base text-gray-300 max-w-md leading-relaxed">
              {t("Verwalten und überwachen Sie Fahrzeuge, Maschinen, Messgeräte, PCs, Laptops und Mobilgeräte zentral. Echtzeit-Status, automatische Updates – aus Daten werden Entscheidungen.", "Manage and monitor vehicles, machines, measuring devices, PCs, laptops and mobile devices centrally. Real-time status, automatic updates – turning data into decisions.")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button data-testid="a-hero-demo" onClick={openDemo} className="inline-flex items-center justify-center gap-2 text-white font-bold rounded-xl px-6 py-3.5 text-sm transition-all hover:brightness-110 hover:-translate-y-0.5 shadow-lg" style={{ background: RED, boxShadow: `0 12px 34px -14px ${RED}` }}>
                {t("Demo anfragen", "Request demo")} <ArrowRight className="w-4 h-4" />
              </button>
              <button data-testid="a-hero-video" onClick={openDemo} className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 hover:bg-white/20 text-white font-semibold rounded-xl px-6 py-3.5 text-sm transition-colors backdrop-blur-sm">
                <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: RED }}><Play className="w-3 h-3 text-white fill-current" /></span> {t("Video ansehen", "Watch video")}
              </button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs sm:text-sm text-gray-300">
              {[t("Alle Assets in einer Plattform", "All assets in one platform"), t("Sicher & skalierbar", "Secure & scalable"), t("Enterprise-ready", "Enterprise-ready")].map((x) => (
                <span key={x} className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: "#c00000" }} /> {x}</span>
              ))}
            </div>
          </div>

          {/* Rechts: helles Dashboard (schwebt ueber dem Bild) */}
          <div className="lg:col-span-7 relative z-10">
            <div className="rounded-xl border border-white/10 shadow-2xl overflow-hidden bg-white transition-transform hover:-translate-y-1 duration-300" data-testid="a-hero-dashboard">
              <DashboardLight />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          {[[Truck, t("Flotten-Monitoring", "Fleet monitoring"), t("Fahrzeuge und Maschinen in Echtzeit – wo sie sind, wie sie genutzt werden und wann Handlung nötig ist.", "Vehicles and machines in real time – where they are, how they're used and when action is needed.")], [Activity, t("Geräte-Zustand", "Device condition"), t("Zustand und Status aller Geräte im Blick – von Messgeräten bis Laptops. Früh gewarnt, Ausfälle vermieden.", "Condition and status of all devices in view – from measuring devices to laptops. Warned early, failures avoided.")], [BarChart3, t("Asset-Insights", "Asset insights"), t("Aus Daten werden Entscheidungen: Auswertungen optimieren Auslastung, senken Kosten und planen voraus.", "Turning data into decisions: analytics optimise utilisation, cut costs and plan ahead.")]].map(([Icon, ti, d], i) => (
            <div key={i} data-testid={`a-feature-${i}`} className="group bg-white border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-xl hover:border-[#c00000] transition-all">
              <span className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ background: RED }}><Icon className="w-6 h-6 text-white" /></span>
              <h3 className="text-lg font-bold text-gray-900 mb-2" style={O}>{ti}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{d}</p>
              <ArrowRight className="w-5 h-5 mt-5 transition-transform group-hover:translate-x-1" style={{ color: RED }} />
            </div>
          ))}
        </div>
      </section>

      {/* STATS (Beispielwerte) */}
      <section className="px-4 sm:px-6">
        <div className="max-w-7xl mx-auto border-y border-gray-100 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[["1.200+", t("Unternehmen", "companies"), Users], ["250.000+", t("verwaltete Assets", "managed assets"), Boxes], ["40%", t("weniger Betriebskosten", "lower operating cost"), TrendingUp], ["99,9%", t("Verfügbarkeit", "uptime"), ShieldCheck]].map(([n, l, Icon]) => (
            <div key={l} className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: `${RED}12` }}><Icon className="w-5 h-5" style={{ color: RED }} /></span>
              <div><div className="text-2xl font-extrabold" style={{ ...O, color: RED }}>{n}</div><div className="text-xs text-gray-600">{l}</div></div>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto text-center text-[11px] text-gray-400 mt-2">{t("Illustrative Beispielwerte · Demovorschau", "Illustrative sample values · Demo preview")}</div>
      </section>

      {/* BRANCHEN */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs font-bold tracking-[0.2em] text-gray-400 mb-6">{t("EINGESETZT IN VERSCHIEDENEN BRANCHEN", "USED ACROSS VARIOUS INDUSTRIES")}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {[t("Logistik", "Logistics"), t("Bauwirtschaft", "Construction"), t("Fertigung", "Manufacturing"), t("Handel", "Retail"), t("Öffentlicher Sektor", "Public sector"), t("IT & Facility", "IT & facility")].map((b) => (
              <span key={b} className="text-sm font-semibold text-gray-500 border border-gray-200 rounded-full px-4 py-1.5">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL (Demo) */}
      <section className="relative px-4 sm:px-6 py-16 overflow-hidden" style={{ background: "#1B1E23" }}>
        <img src={ROAD} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />
        <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(90deg,#1B1E23 35%, rgba(27,30,35,0.4))" }} />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <Quote className="w-10 h-10 mb-4" style={{ color: RED }} />
            <p className="text-xl sm:text-2xl text-white font-medium leading-relaxed" style={O}>{t("„FleetArea gibt uns volle Transparenz über unsere gesamte Asset-Landschaft. Es spart Zeit, senkt Kosten und hält den Betrieb am Laufen — jeden Tag.“", "\u201cFleetArea gives us full transparency over our entire asset landscape. It saves time, cuts costs and keeps operations running — every day.\u201d")}</p>
            <div className="mt-5"><div className="text-white font-semibold text-sm">M. W., {t("Flottenleitung", "Head of Fleet")}</div><div className="text-gray-400 text-xs">{t("Beispielhafte Referenz · Demo", "Illustrative reference · Demo")}</div></div>
            <div className="mt-4 flex gap-1.5">{[0, 1, 2].map((i) => <span key={i} className="w-2 h-2 rounded-full" style={{ background: i === 0 ? RED : "#4b5563" }} />)}</div>
          </div>
          <div className="hidden lg:block text-right text-white/85 text-sm font-bold tracking-[0.15em] leading-relaxed" style={O}>{t("MEHR KONTROLLE.", "MORE CONTROL.")}<br />{t("EIN PRODUKTIVERES MORGEN.", "A MORE PRODUCTIVE TOMORROW.")}</div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />

      {/* DEMO-DIALOG */}
      <DemoDialog open={demo} onClose={() => setDemo(false)} />
    </div>
  );
}

const SIDE = [
  [LayoutGrid, "Übersicht", "Overview", true], [Truck, "Fahrzeuge", "Vehicles"], [Wrench, "Maschinen", "Machines"], [Gauge, "Messgeräte", "Measuring"],
  [Cpu, "IT-Geräte", "IT devices"], [MapPin, "Standorte", "Locations"], [RefreshCw, "Updates", "Updates"], [Bell, "Alerts", "Alerts"], [FileBarChart, "Berichte", "Reports"], [Settings, "Administration", "Administration"],
];

function DashboardLight() {
  const { lang } = useLang();
  const de = lang === "de";
  const t = (d, e) => (de ? d : e);
  const kpis = [[t("Assets gesamt", "Total assets"), "1.284", "+12%", "#10b981"], [t("Online", "Online"), "1.156", "90%", "#10b981"], [t("Alerts", "Alerts"), "28", "-40%", RED], [t("Updates offen", "Updates open"), "62", "-18%", "#f59e0b"]];
  const legend = [[t("Fahrzeuge", "Vehicles"), "412", RED], [t("Maschinen", "Machines"), "219", "#f59e0b"], [t("Messgeräte", "Measuring"), "98", "#10b981"], [t("PCs & Laptops", "PCs & laptops"), "342", "#3b82f6"], [t("Mobilgeräte", "Mobile"), "213", "#8b5cf6"]];
  const acts = [
    [t("Fahrzeug D-AL 742", "Vehicle D-AL 742"), t("Standort aktualisiert", "Location updated"), t("vor 2 Min.", "2 min ago")],
    [t("Laptop LT-338", "Laptop LT-338"), t("Sicherheitsupdate installiert", "Security update installed"), t("vor 12 Min.", "12 min ago")],
    [t("Bagger EX-17", "Excavator EX-17"), t("Wartung fällig in 5 Tagen", "Maintenance due in 5 days"), t("vor 28 Min.", "28 min ago")],
  ];
  return (
    <div className="flex text-left" style={{ fontSize: 11 }}>
      {/* Sidebar */}
      <aside className="hidden sm:flex flex-col w-40 shrink-0 bg-white border-r border-gray-200 text-gray-500 py-3">
        <div className="px-3 mb-3"><LogoA small /></div>
        <nav className="px-2 space-y-0.5">
          {SIDE.map(([Icon, dl, el, active]) => (
            <div key={dl} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md ${active ? "font-semibold" : "text-gray-500"}`} style={active ? { background: `${RED}12`, color: RED } : undefined}>
              <Icon className="w-3.5 h-3.5" /> <span className="text-[11px]">{de ? dl : el}</span>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 bg-[#f7f8fa] min-w-0">
        {/* Topbar */}
        <div className="flex items-center gap-2 px-3 h-9 bg-white border-b border-gray-200">
          <div className="flex items-center gap-1.5 flex-1 max-w-xs bg-gray-100 rounded-md px-2 py-1 text-gray-400"><Search className="w-3 h-3" /><span className="text-[10px]">{t("Assets, Standorte oder Geräte…", "Assets, locations or devices…")}</span></div>
          <div className="ml-auto flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-gray-800 text-white text-[9px] flex items-center justify-center font-bold">MM</span>
            <div className="hidden md:block leading-tight"><div className="text-[10px] font-semibold text-gray-800">Max Mustermann</div><div className="text-[8px] text-gray-400">{t("Flottenmanager", "Fleet manager")}</div></div>
          </div>
        </div>

        <div className="p-3">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <div className="text-sm font-bold text-gray-900" style={O}>{t("Übersicht", "Overview")}</div>
              <div className="text-[9px] text-gray-400">{t("Alle Assets. Echtzeit. Unter Kontrolle.", "All assets. Real time. In control.")}</div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-500">{t("Produktvorschau · Demodaten", "Product preview · demo data")}</span>
              <span className="text-[9px] px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-600 flex items-center gap-1">{t("Letzte 30 Tage", "Last 30 days")} <ChevronDown className="w-2.5 h-2.5" /></span>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-4 gap-2 mb-2.5">
            {kpis.map(([l, v, d, c]) => (
              <div key={l} className="bg-white border border-gray-200 rounded-lg p-2">
                <div className="text-[8px] uppercase tracking-wide text-gray-400">{l}</div>
                <div className="text-sm font-extrabold text-gray-900 mt-0.5" style={O}>{v}</div>
                <div className="text-[9px] font-semibold" style={{ color: c }}>{d}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            {/* Donut + Legende */}
            <div className="bg-white border border-gray-200 rounded-lg p-2.5">
              <div className="text-[10px] font-semibold text-gray-700 mb-1.5">{t("Assets nach Typ", "Assets by type")}</div>
              <div className="flex items-center gap-3">
                <div className="relative w-16 h-16 rounded-full shrink-0" style={{ background: `conic-gradient(${RED} 0 32%, #f59e0b 32% 49%, #10b981 49% 57%, #3b82f6 57% 84%, #8b5cf6 84% 100%)` }}>
                  <div className="absolute inset-[7px] rounded-full bg-white flex flex-col items-center justify-center leading-none"><div className="text-[11px] font-extrabold text-gray-900">1.284</div><div className="text-[7px] text-gray-400">{t("gesamt", "total")}</div></div>
                </div>
                <div className="space-y-0.5 flex-1">
                  {legend.map(([n, c, col]) => (
                    <div key={n} className="flex items-center gap-1.5 text-[9px]"><span className="w-1.5 h-1.5 rounded-full" style={{ background: col }} /><span className="text-gray-600 flex-1">{n}</span><span className="text-gray-800 font-semibold">{c}</span></div>
                  ))}
                </div>
              </div>
            </div>
            {/* Karte */}
            <div className="bg-white border border-gray-200 rounded-lg p-2.5">
              <div className="text-[10px] font-semibold text-gray-700 mb-1.5">{t("Asset-Standorte", "Asset locations")}</div>
              <div className="relative h-[76px] rounded-md overflow-hidden bg-[#eef1f4]" style={{ backgroundImage: "linear-gradient(rgba(0,0,0,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,.05) 1px,transparent 1px)", backgroundSize: "16px 16px" }}>
                {[["22%", "40%"], ["45%", "62%"], ["63%", "30%"], ["78%", "55%"], ["38%", "24%"]].map(([l, t], i) => (
                  <span key={i} className="absolute w-1.5 h-1.5 rounded-full" style={{ left: l, top: t, background: RED, boxShadow: `0 0 0 3px ${RED}30` }} />
                ))}
                <div className="absolute top-1 right-1 bg-white rounded shadow px-1.5 py-0.5 text-[8px] text-gray-700 border border-gray-100">LKW D-AL 742 · Online</div>
              </div>
            </div>
          </div>

          {/* Aktivität + LKW-Detailkarte */}
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div className="col-span-2 bg-white border border-gray-200 rounded-lg p-2.5">
              <div className="text-[10px] font-semibold text-gray-700 mb-1.5">{t("Letzte Aktivität", "Recent activity")}</div>
              <div className="space-y-1">
                {acts.map(([n, txt, time]) => (
                  <div key={n} className="flex items-center gap-2 text-[9px]">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: RED }} />
                    <span className="text-gray-800 font-medium">{n}</span>
                    <span className="text-gray-400 truncate">— {txt}</span>
                    <span className="ml-auto text-gray-400 shrink-0">{time}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-1 bg-white border border-gray-200 rounded-lg p-2.5 flex flex-col" data-testid="a-truck-card">
              <div className="relative h-12 rounded-md overflow-hidden mb-1.5 flex items-center justify-center" style={{ background: `${RED}0d` }}>
                <Truck className="w-8 h-8" style={{ color: RED }} />
                <span className="absolute top-1 right-1 flex items-center gap-1 bg-white rounded-full px-1.5 py-0.5 text-[7px] font-semibold text-emerald-600 border border-gray-100"><span className="w-1 h-1 rounded-full bg-emerald-500" /> {t("Online", "Online")}</span>
              </div>
              <div className="text-[10px] font-bold text-gray-900" style={O}>D-AL 742</div>
              <div className="text-[8px] text-gray-400">Mercedes-Benz Actros</div>
              <div className="mt-auto pt-1.5 flex items-center justify-between text-[8px]"><span className="text-gray-500">{t("Geschwindigkeit", "Speed")}</span><span className="font-bold text-gray-800">56 km/h</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
